import { formatDate, formatMoney, plural } from "../../lib/format";

const Totals = ({ p, clients }) => {
  const a = p.actual;
  const r = p.resultado;
  const rows = [
    ["Clientes", p.clientes_total_actual.toLocaleString("es-MX"), clients.toLocaleString("es-MX"), "clientes"],
    ["Créditos", a.creditos.toLocaleString("es-MX"), r.creditos.toLocaleString("es-MX"), "creditos"],
    ["Outstanding Balance MXN", formatMoney(a.saldo_mxn), formatMoney(r.saldo_mxn), "saldo"],
    ["Intereses cobrados (sin IVA)", formatMoney(a.intereses_cobrados), formatMoney(r.intereses_cobrados), "intereses"],
    [`Month Revenue · ${r.month_label}`, formatMoney(a.month_revenue), formatMoney(r.month_revenue), "revenue"],
    ["Fecha de corte", formatDate(a.fecha_corte), formatDate(r.fecha_corte), "corte"],
  ];
  return (
    <table className="w-full text-xs" data-testid="review-totals">
      <thead className="text-[11px] uppercase tracking-wide text-[#5b667a]">
        <tr><th className="py-1 text-left">Totales</th><th className="py-1 text-right">Hoy</th><th className="py-1 text-right">Después de aplicar</th></tr>
      </thead>
      <tbody className="divide-y divide-[#eef2f8]">
        {rows.map(([label, now, after, k]) => (
          <tr key={k}>
            <td className="py-1.5 font-semibold text-[#3b475c]">{label}</td>
            <td className="py-1.5 text-right tabular-nums text-[#5b667a]">{now}</td>
            <td className="py-1.5 text-right font-bold tabular-nums text-[#182235]" data-testid={`review-total-${k}`}>{after}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Section = ({ title, items, testId, render, tone = "", hint, open = false }) => (
  <details open={open} className={`rounded-[12px] border px-3 py-2 ${tone || "border-[#e8edf5] bg-[#f8fafd]"}`} data-testid={testId}>
    <summary className="cursor-pointer text-sm font-bold text-[#182235]">
      {title} (<span data-testid={`${testId}-count`}>{items.length}</span>)
    </summary>
    {hint && <p className="mt-1 text-[11px] text-[#5b667a]">{hint}</p>}
    <ul className="mt-1.5 max-h-48 space-y-1 overflow-y-auto text-xs text-[#3b475c]">
      {items.length ? items.map((it, i) => <li key={i} data-testid={`${testId}-item-${i}`}>{render(it, i)}</li>) : <li className="text-[#8a94a6]">Ninguno</li>}
    </ul>
  </details>
);

const Check = ({ on, onChange, testId, children }) => (
  <label className="flex cursor-pointer items-start gap-2">
    <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-[#0f5ef7]" data-testid={testId} />
    <span>{children}</span>
  </label>
);

const Counter = ({ label, value, k }) => (
  <div className="rounded-[12px] border border-[#e8edf5] bg-[#f8fafd] px-3 py-2">
    <div className="text-lg font-extrabold text-[#182235]" data-testid={`review-${k}`}>{value}</div>
    <div className="text-xs font-semibold text-[#5b667a]">{label}</div>
  </div>
);

export const ImportReviewPanel = ({ p, off, toggle }) => {
  const a = p.arreglo;
  const kept = p.no_reales.filter((x) => off.no_reales.has(x.borrower_id)).map((x) => ({ from: x.borrower_id, to: x.new_id, name: x.name, persona: "PF", excel_name: "" }));
  const ids = [...p.cambios_id, ...kept];
  const serie = [...p.serie_9000, ...kept];
  const offPartials = p.parciales.filter((x) => off.parciales.has(x.key)).length;
  const dissolved = p.no_reales.filter((x) => !off.no_reales.has(x.borrower_id)).length;
  const clients = p.clientes_total_actual + p.clientes_nuevos.length + offPartials - dissolved;
  const trayFiles = a.bandeja.length;
  return (
    <div className="space-y-2.5" data-testid="import-review">
      <p className="rounded-[10px] bg-[#eef4ff] px-3 py-2 text-xs font-semibold text-[#0f3c99]">Revisión: todavía no se ha guardado nada. Pulsa "Aplicar" para hacer todos los cambios a la vez.</p>
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <Counter label="Créditos nuevos" value={p.creditos_nuevos.length} k="creditos-nuevos" />
        <Counter label="Créditos actualizados" value={p.creditos_actualizados} k="creditos-actualizados" />
        <Counter label="Créditos eliminados" value={0} k="creditos-eliminados" />
        <Counter label="Clientes nuevos" value={p.clientes_nuevos.length + offPartials} k="clientes-nuevos" />
      </div>
      <p className="text-xs text-[#3b475c]" data-testid="review-pagos">
        {p.hoja_pagos ? `Pagos reemplazados en ${plural(p.creditos_en_archivo, "crédito", "créditos")} · fecha de corte ${formatDate(p.fecha_corte)}` : "Sin hoja de pagos: se conservan saldos y fechas de corte."}
        {p.hoja_calendario ? " · calendarios reemplazados" : ""}
        {p.fechas_disposicion_cambiadas ? ` · ${plural(p.fechas_disposicion_cambiadas, "fecha de disposición cambia", "fechas de disposición cambian")}` : ""}
      </p>
      <Section title="Clientes vinculados" items={p.vinculados} testId="review-vinculados"
        hint="Mismo nombre que un cliente creado desde el ZIP: toma el ID, nombre y tipo del Excel y conserva sus archivos."
        render={(v) => <>{v.from === v.to ? v.to : <>{v.from} → <b>{v.to}</b></>} · {v.excel_name} ({v.persona})</>} />
      <Section title="Coincidencias parciales" items={p.parciales} testId="review-parciales" open
        hint="Todas las palabras del cliente del ZIP están en el nombre del Excel. Marcadas se vinculan; desmarcadas quedan en Posibles duplicados."
        render={(x, i) => (
          <Check on={!off.parciales.has(x.key)} onChange={(v) => toggle("parciales", x.key, v)} testId={`review-parcial-check-${i}`}>
            {x.name} ({x.from}) → <b>{x.excel_name} {x.to}</b>
          </Check>
        )} />
      <Section title="Clientes del ZIP que cambian de ID" items={ids} testId="review-cambios-id" tone="border-[#f1d9a8] bg-[#fff8eb]"
        hint="Tienen un ID que el Excel usa para otra persona: conservan nombre, tipo y archivos con un ID nuevo de la serie 9000."
        render={(c) => <>{c.name}: {c.from} → <b>{c.to}</b> <span className="text-[#5b667a]">(el Excel usa {c.from} para {c.excel_name || "otro cliente"})</span></>} />
      <Section title="Clientes de la intranet que pasan a numeración 9000" items={serie} testId="review-serie-9000"
        hint="Todos los clientes creados en la intranet que no se vinculan a un ID del Excel. Solo cambia el ID; los archivos no se copian ni se vuelven a subir."
        render={(c) => <>{c.from} → <b>{c.to}</b> · {c.name} ({c.persona})</>} />
      <Section title={`Por clasificar → clientes del Excel: ${plural(trayFiles, "archivo irá", "archivos irán")} a clientes del Excel`}
        items={a.bandeja_por_cliente} testId="review-bandeja" render={(x) => <>{x.to_name} ({x.to}): {plural(x.files, "archivo", "archivos")}</>} />
      <Section title="Archivos a reacomodar" items={a.reacomodar} testId="review-reacomodar"
        hint="El nombre del archivo trae el nombre completo de un cliente del Excel distinto al actual. Los documentos de aval nunca se mueven."
        render={(x, i) => (
          <Check on={!off.reacomodar.has(x.id)} onChange={(v) => toggle("reacomodar", x.id, v)} testId={`review-reacomodar-check-${i}`}>
            {x.filename} · {x.from_name} → <b>{x.to_name} ({x.to})</b>
          </Check>
        )} />
      <Section title="Propuestas de IA resueltas" items={a.propuestas} testId="review-propuestas"
        render={(x) => <>{x.name} → <b>{x.to_name} ({x.to})</b> · {plural(x.files, "archivo", "archivos")}</>} />
      <Section title="Clientes no reales" items={p.no_reales} testId="review-no-reales"
        hint="Se reparten sus archivos por nombre de archivo; lo que no coincida va a Por clasificar. Después se elimina el cliente vacío."
        render={(x, i) => {
          const f = a.no_reales.find((n) => n.old_id === x.borrower_id);
          return (
            <Check on={!off.no_reales.has(x.borrower_id)} onChange={(v) => toggle("no_reales", x.borrower_id, v)} testId={`review-no-real-check-${i}`}>
              "{x.name}" ({x.borrower_id}) · {f ? `${plural(f.files, "archivo", "archivos")}: ${f.asignados.map((d) => `${d.files} a ${d.to_name}`).join(", ") || "ninguno a clientes"}; ${f.por_clasificar} a Por clasificar` : "se conserva"}
            </Check>
          );
        }} />
      <Section title="Posibles duplicados (no se vinculan)" items={p.posibles_duplicados} testId="review-posibles-duplicados"
        render={(d) => <>{d.borrower_id} {d.name} ≈ {d.similares.map((s) => `${s.borrower_id} ${s.name}`).join(", ")}</>} />
      <Section title="Clientes nuevos" items={p.clientes_nuevos} testId="review-clientes-nuevos" render={(c) => <>{c.borrower_id} · {c.name}</>} />
      <Section title="Créditos nuevos" items={p.creditos_nuevos} testId="review-creditos-nuevos-lista" render={(x) => x} />
      <div className="rounded-[12px] border border-[#e8edf5] px-3 py-2"><Totals p={p} clients={clients} /></div>
    </div>
  );
};
